import React, { HTMLAttributes, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { getAvailablePaymentMethodType } from "../../../apis/subscription";
import type { IGetPaymentGatewayTypeResponse } from "../../../apis/subscription/type";
import { ReactComponent as AlipayhkIcon } from "../../../assets/icons/Alipayhk.svg";
import { ReactComponent as FPSIcon } from "../../../assets/icons/FPS.svg";
import { ReactComponent as PaymeIcon } from "../../../assets/icons/Payme.svg";
import { ReactComponent as CreditCardIcon } from "../../../assets/icons/creditCard.svg";

import AdyenAlipayHK from "../../atom/adyenAlipayHK";
import AdyenCreditCardElement, {
  type IAdyenCreditCardInfo,
} from "../../atom/adyenCreditCard";
import AdyenPayme from "../../atom/adyenPayme";
import AirWallexFPS from "../../atom/airwallexFPS";
import BaseLabel from "../../atom/baseLabel";

export interface IPaymentMethodsInfo {
  paymentMethodToken: string;
  adyenCreditCardInfo: IAdyenCreditCardInfo;
}

interface ISubscriptionPaymentMethodSelectionBlock {
  adyenCheckout: (value?: IPaymentMethodsInfo) => void;
  buttonDisabled?: boolean;
  reInitPaymentMethod?: boolean;
  returnUrl?: string;
  paymentId?: string;
  createSubscriptionCallback?: (paymentMethodToken: string) => void;
}

type TSubscriptionPaymentMethodSelectionBlockProps =
  ISubscriptionPaymentMethodSelectionBlock & HTMLAttributes<HTMLDivElement>;

const SubscriptionPaymentMethodSelectionBlock: React.FC<
  TSubscriptionPaymentMethodSelectionBlockProps
> = (props) => {
  const {
    adyenCheckout,
    buttonDisabled = false,
    reInitPaymentMethod,
    returnUrl,
    paymentId,
    createSubscriptionCallback,

    ...otherProps
  } = props;

  // available payment methods
  const [availablePaymentMethod, setAvailablePaymentMethod] = useState<
    IGetPaymentGatewayTypeResponse[] | undefined | null
  >(undefined);

  // selected payment method
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    IGetPaymentGatewayTypeResponse | undefined
  >(undefined);

  const fetchAvailablePaymentMethods = async () => {
    try {
      const methodList = await getAvailablePaymentMethodType();
      setAvailablePaymentMethod(methodList);
    } catch (error) {}
  };

  useEffect(() => {
    fetchAvailablePaymentMethods();
  }, []);

  // reinitialize payment method
  useEffect(() => {
    if (reInitPaymentMethod) {
      console.log("reinit payment method");
      setSelectedPaymentMethod(undefined);
    }
  }, [reInitPaymentMethod]);

  // create non credit card subscription before adyen checkout
  useEffect(() => {
    if (
      selectedPaymentMethod &&
      selectedPaymentMethod?.id !== 5 &&
      createSubscriptionCallback
    ) {
      console.log("it is not credit card");
      if (!paymentId) {
        console.log("first time create subscription");
        console.log(`createSubscriptionCallback`);
        createSubscriptionCallback(selectedPaymentMethod?.token!);
      }
    }
  }, [selectedPaymentMethod, paymentId]);

  return (
    <div className="flex flex-col w-full gap-4 text-black" {...otherProps}>
      <BaseLabel text={"請選擇付款方式"} />
      <div className="flex flex-row items-center gap-2">
        {availablePaymentMethod?.map((method) => {
          return (
            <div
              key={method.id}
              className={twMerge(
                "flex p-1 bg-white rounded cursor-pointer h-[36px] w-[56px]",
                selectedPaymentMethod?.id === method.id
                  ? "border-mainYellow border-[3px]"
                  : "border-white border-[3px]"
              )}
              onClick={() => {
                setSelectedPaymentMethod(method);
              }}
            >
              {method.id === 5 && <CreditCardIcon width={48} />}
              {method.id === 9 && <AlipayhkIcon width={48} />}
              {method.id === 10 && <PaymeIcon width={48} />}
              {method.id === 6 && <FPSIcon width={48} />}
            </div>
          );
        })}
      </div>

      {/* init credit card component */}
      {selectedPaymentMethod?.id === 5 && (
        <AdyenCreditCardElement
          onCardElementSubmit={(state) =>
            adyenCheckout({
              paymentMethodToken: selectedPaymentMethod.token,
              adyenCreditCardInfo: state,
            })
          }
          buttonDisabled={buttonDisabled}
        />
      )}

      {/* init alipayhk component */}
      {selectedPaymentMethod?.id === 9 && (
        <AdyenAlipayHK paymentId={paymentId} returnUrl={returnUrl} />
      )}

      {/* init payme component */}
      {selectedPaymentMethod?.id === 10 && (
        <AdyenPayme paymentId={paymentId} returnUrl={returnUrl} />
      )}

      {/* init FPS component */}
      {selectedPaymentMethod?.id === 6 && (
        <AirWallexFPS paymentId={paymentId} returnUrl={returnUrl} />
      )}
    </div>
  );
};

export default SubscriptionPaymentMethodSelectionBlock;
